import clsx from 'clsx'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import type SvgIcon from 'LuxKit/SvgIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useTourStyledLabels from 'hooks/Tours/useTourStyledLabels'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div`
  position: relative;
  padding: ${rem(12)} ${rem(20)} ${rem(28)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(20)} ${rem(28)};
  }

  &.is-current {
    padding-top: ${rem(20)};
    border-color: ${props => props.theme.palette.neutral.surface.darkForeground}
  }
`

const CurrentLabel = styled(StructureLabel)`
  position: absolute;
  top: -1px;
  inset-inline-start: -1px;
  transform: translateY(-50%);
`

interface TourStyleBenefit {
  Icon: typeof SvgIcon
  description: string
}

interface TourStyleContent {
  subtitle: string
  benefits: Array<TourStyleBenefit>
}

interface Props {
  productType: Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2SignatureSeriesProductType | App.Tours.V2InsiderSeriesProductType>
  isCurrent: boolean
  showGroupBenefit: boolean
}

function TourStyledNonSignatureProductTypeInfoSheet({ productType, isCurrent, showGroupBenefit }: Props) {
  const intl = useIntl()
  const { styledProductTypeTitles, styledProductTypeHotelBenefits } = useTourStyledLabels()

  const tourStylesBenefits = useMemo<Record<Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2SignatureSeriesProductType | App.Tours.V2InsiderSeriesProductType>, TourStyleContent>>(() => ({
    deluxe_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'tbnudKpZh6ASgHghTiQ6',
          defaultMessage: 'Discover the highlights, effortlessly',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: styledProductTypeHotelBenefits.deluxe_tour },
        ...(
          showGroupBenefit
            ? [
                {
                  Icon: LineUsersAltIcon,
                  description: intl.formatMessage(
                    {
                      id: '-DdgBFavQ2_rTsbhJyXL',
                      defaultMessage: 'Max 24 travellers',
                    },
                  ),
                },
              ]
            : []
        ),
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'nStO9BAu9vjNVS8By7Gk',
              defaultMessage: 'Unmissable experiences',
            },
          ),
        },
      ],
    },
    premium_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'VXZd0ub0-FLHI4qMiQph',
          defaultMessage: 'Elevated experiences, elevated accommodation',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: styledProductTypeHotelBenefits.premium_tour },
        ...(
          showGroupBenefit
            ? [
                {
                  Icon: LineUsersAltIcon,
                  description: intl.formatMessage(
                    {
                      id: 'KqGbmIaXXK-UQkjuixcr',
                      defaultMessage: 'Max 16 travellers',
                    },
                  ),
                },
              ]
            : []
        ),
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'y_fySsJbSNvtCHKRD_2F',
              defaultMessage: 'Dedicated agent',
            },
          ),
        },
      ],
    },
    ultra_lux_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'KMp-kqdyfbBPelJ7rylZ',
          defaultMessage: 'Only the best, from start to finish',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: styledProductTypeHotelBenefits.ultra_lux_tour },
        ...(
          showGroupBenefit
            ? [
                {
                  Icon: LineUsersAltIcon,
                  description: intl.formatMessage(
                    {
                      id: 'w4bQi0z1CvuD4KV7akXT',
                      defaultMessage: 'Max 12 travellers',
                    },
                  ),
                },
              ]
            : []
        ),
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'mEMVMdkeBdMLmzK68ejX',
              defaultMessage: 'Signature dining experiences',
            },
          ),
        },
      ],
    },
    custom_tour: {
      subtitle: '',
      benefits: [],
    },
  }), [intl, showGroupBenefit, styledProductTypeHotelBenefits])

  const content = tourStylesBenefits[productType]

  return <Container className={clsx({ 'is-current': isCurrent })}>
    {isCurrent && <CurrentLabel variant="standard">{intl.formatMessage(
      {
        id: '7yXkfuu8XlRlsdQm3Gxd',
        defaultMessage: 'Current tour',
      },
    )}
    </CurrentLabel>}
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <Heading variant="heading4" colour="normal-contrast">
          {styledProductTypeTitles[productType]}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          {content.subtitle}
        </BodyText>
      </Group>
      <Group direction="vertical" gap={8}>
        {content.benefits.map((benefit) => <BodyTextBlock
          key={benefit.description}
          colour="light-foreground-alt"
          variant="medium"
          startIcon={<benefit.Icon />}
        >
          {benefit.description}
        </BodyTextBlock>)}
      </Group>
    </Group>
  </Container>
}

export default TourStyledNonSignatureProductTypeInfoSheet
